import React, { createContext, useContext, useId, useState } from 'react';
import styles from './Tabs.module.css';

export type TabsVariant = 'underline' | 'pill';
export type TabsSize = 'sm' | 'md' | 'lg';

interface TabsContextValue {
  value: string;
  onChange: (value: string) => void;
  variant: TabsVariant;
  size: TabsSize;
  baseId: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('Tab components must be used inside <Tabs>');
  return ctx;
}

// ── Tabs (root) ──────────────────────────────────────────────────────────────

export interface TabsProps {
  /** Controlled active tab value */
  value?: string;
  /** Default active tab for uncontrolled usage */
  defaultValue?: string;
  onChange?: (value: string) => void;
  variant?: TabsVariant;
  size?: TabsSize;
  children: React.ReactNode;
  className?: string;
}

function Tabs({
  value: controlledValue,
  defaultValue = '',
  onChange,
  variant = 'underline',
  size = 'md',
  children,
  className,
}: TabsProps) {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const baseId = useId();

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  function handleChange(next: string) {
    if (!isControlled) setInternalValue(next);
    onChange?.(next);
  }

  return (
    <TabsContext.Provider value={{ value, onChange: handleChange, variant, size, baseId }}>
      <div className={[styles.root, className].filter(Boolean).join(' ')}>{children}</div>
    </TabsContext.Provider>
  );
}

// ── TabList ──────────────────────────────────────────────────────────────────

export interface TabListProps {
  children: React.ReactNode;
  className?: string;
}

function TabList({ children, className }: TabListProps) {
  const { variant } = useTabsContext();
  return (
    <div
      role="tablist"
      className={[styles.tabList, styles[variant], className].filter(Boolean).join(' ')}
    >
      {children}
    </div>
  );
}

// ── Tab ──────────────────────────────────────────────────────────────────────

export interface TabProps {
  value: string;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
  /** Icon placed before the label */
  icon?: React.ReactNode;
}

function Tab({ value, disabled = false, children, className, icon }: TabProps) {
  const { value: activeValue, onChange, variant, size, baseId } = useTabsContext();
  const isActive = value === activeValue;

  return (
    <button
      role="tab"
      id={`${baseId}-tab-${value}`}
      aria-controls={`${baseId}-panel-${value}`}
      aria-selected={isActive}
      disabled={disabled}
      tabIndex={isActive ? 0 : -1}
      onClick={() => onChange(value)}
      className={[
        styles.tab,
        styles[variant],
        styles[size],
        isActive && styles.active,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {icon && <span className={styles.tabIcon}>{icon}</span>}
      {children}
    </button>
  );
}

// ── TabPanels ─────────────────────────────────────────────────────────────────

export interface TabPanelsProps {
  children: React.ReactNode;
  className?: string;
}

function TabPanels({ children, className }: TabPanelsProps) {
  return <div className={[styles.panels, className].filter(Boolean).join(' ')}>{children}</div>;
}

// ── TabPanel ─────────────────────────────────────────────────────────────────

export interface TabPanelProps {
  value: string;
  children: React.ReactNode;
  className?: string;
  /** Keep the panel mounted in the DOM when inactive (default: false) */
  keepMounted?: boolean;
}

function TabPanel({ value, children, className, keepMounted = false }: TabPanelProps) {
  const { value: activeValue, baseId } = useTabsContext();
  const isActive = value === activeValue;

  if (!isActive && !keepMounted) return null;

  return (
    <div
      role="tabpanel"
      id={`${baseId}-panel-${value}`}
      aria-labelledby={`${baseId}-tab-${value}`}
      hidden={!isActive}
      className={[styles.panel, className].filter(Boolean).join(' ')}
    >
      {children}
    </div>
  );
}

// ── Exports ──────────────────────────────────────────────────────────────────

export { Tabs, TabList, Tab, TabPanels, TabPanel };
